import { displayPortCode, formatPosition, normalizeSearch } from '@felix/domain';
import { ActionIcon, Button, Menu, Tabs, TextInput } from '@mantine/core';
import { IconDots, IconFileImport, IconPlus, IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import purchasing from '../purchasing/Purchasing.module.css';
import { sortItems } from '../purchasing/requisitions.js';
import {
  LoadError,
  NoMatch,
  SortableTh,
  type SortState,
  TablePager,
  TableSkeleton,
} from '../purchasing/table.js';
import { formatStamp } from '../voyage/NoonSections.js';
import { useNumber } from '../voyage/numbers.js';
import { useCountryName, usePortCatalog } from './catalog-context.js';
import {
  CompanyPortDialog,
  DistanceDialog,
  HistoryDialog,
  ImportDialog,
} from './PortDistanceDialogs.js';
import classes from './Route.module.css';
import { SourceBadge } from './SourceBadge.js';
import type { PortsSource } from './source.js';
import type { CompanyPort, PortDistance } from './types.js';

const PAGE_SIZE = 15;

type Column = 'from' | 'to' | 'distance' | 'source' | 'updated';
type Notice = { tone: 'success' | 'danger'; text: string };

const SOURCE_ORDER = { manual: 0, imported: 1, computed: 2 } as const;

export interface PortDistancesScreenProps {
  source: PortsSource;
}

/**
 * Voyage ▸ Port Distances (ADR 0048 §3, §6): şirketin mesafe tablosu ve şirket limanları. Ofiste
 * elle ekleme, CSV içe aktarma, hesaplananı saklama, geçmiş ve kaldırma; gemide salt okunur.
 * Görsel referansı yok; tablo dili Suppliers ekranıyla aynı.
 */
export function PortDistancesScreen({ source }: PortDistancesScreenProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`ports.distances.${key}`);
  const [tab, setTab] = useState<string | null>('distances');
  return (
    <div className={purchasing.screen}>
      <PageHeader title={k('title')} description={k('description')} />
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="distances">{k('tabs.distances')}</Tabs.Tab>
          <Tabs.Tab value="ports">{k('tabs.ports')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="distances" pt="md">
          <DistanceTable source={source} />
        </Tabs.Panel>
        <Tabs.Panel value="ports" pt="md">
          <CompanyPortTable source={source} />
        </Tabs.Panel>
      </Tabs>
      <p className={classes.footnote}>{t('ports.approx')}</p>
    </div>
  );
}

function DistanceTable({ source }: { source: PortsSource }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.distances.${key}`, options);
  const errorText = (code: string) =>
    t(`ports.errors.${code}`, { defaultValue: t('ports.errors.GENERIC') });
  const { catalog } = usePortCatalog();
  const countryName = useCountryName();
  const editor = source.editor;
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; rows: PortDistance[] }
  >({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState<Column>>({ key: 'from', dir: 'asc' });
  const [dialog, setDialog] = useState<
    | { kind: 'edit'; row: PortDistance | null }
    | { kind: 'history'; row: PortDistance }
    | { kind: 'import' }
    | null
  >(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const tableBox = useFitColumns<HTMLElement>();

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', rows: await source.distances() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const nameOf = useCallback((code: string) => catalog?.byCode.get(code)?.name ?? code, [catalog]);
  const rows = state.kind === 'ready' ? state.rows : [];
  const items = useMemo(() => {
    const q = normalizeSearch(query);
    const found = rows.filter(
      (row) =>
        !q ||
        [row.portA, row.portB, nameOf(row.portA), nameOf(row.portB), row.note ?? ''].some((value) =>
          normalizeSearch(value).includes(q),
        ),
    );
    const value: Record<Column, (row: PortDistance) => string | number | null> = {
      from: (row) => `${nameOf(row.portA)} ${nameOf(row.portB)}`,
      to: (row) => nameOf(row.portB),
      distance: (row) => row.distanceNm,
      source: (row) => SOURCE_ORDER[row.source],
      updated: (row) => row.updatedAt,
    };
    return sortItems(found, sort.dir, value[sort.key]);
  }, [rows, query, sort, nameOf]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const portLabel = (code: string) => {
    const port = catalog?.byCode.get(code);
    return (
      <div className={classes.portCell}>
        <span className={classes.portName}>
          {port?.name ?? code}
          {displayPortCode(code) ? <span className={classes.code}>{code}</span> : null}
        </span>
        {port ? <span className={classes.portRole}>{countryName(port.country)}</span> : null}
      </div>
    );
  };
  const remove = async (row: PortDistance) => {
    if (!editor) return;
    try {
      await editor.removeDistance(row.id, row.rowVersion);
      setNotice({ tone: 'success', text: k('removed') });
      await load();
    } catch (error) {
      setNotice({ tone: 'danger', text: errorText(errorCode(error)) });
    }
  };
  const recompute = async (row: PortDistance) => {
    if (!editor) return;
    try {
      await editor.updateDistance(row.id, {
        source: 'computed',
        rowVersion: row.rowVersion,
        note: row.note,
      });
      setNotice({ tone: 'success', text: k('saved') });
      await load();
    } catch (error) {
      setNotice({ tone: 'danger', text: errorText(errorCode(error)) });
    }
  };

  return (
    <div className={classes.tabs}>
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <Panel flush aria-label={k('table')}>
        <div className={purchasing.searchRow}>
          <TextInput
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
          />
          {editor ? (
            <div className={classes.actions}>
              <Button
                variant="default"
                leftSection={<IconFileImport size={16} />}
                onClick={() => setDialog({ kind: 'import' })}
              >
                {k('import')}
              </Button>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={() => setDialog({ kind: 'edit', row: null })}
              >
                {k('add')}
              </Button>
            </div>
          ) : null}
        </div>
        {state.kind === 'error' ? (
          <LoadError title={k('error')} code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={6} label={k('table')} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={k('emptyTitle')}
            description={editor ? k('emptyText') : k('emptyShip')}
          />
        ) : items.length === 0 ? (
          <NoMatch
            title={k('noMatch')}
            onClear={() => {
              setQuery('');
              setPage(1);
            }}
          />
        ) : (
          <>
            <section
              ref={tableBox}
              className={table.tableWrap}
              aria-label={k('table')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${purchasing.table}`}>
                <thead>
                  <tr>
                    <SortableTh
                      column="from"
                      label={k('columns.from')}
                      sort={sort}
                      onSort={setSort}
                    />
                    <SortableTh column="to" label={k('columns.to')} sort={sort} onSort={setSort} />
                    <SortableTh
                      column="distance"
                      label={k('columns.distance')}
                      sort={sort}
                      onSort={setSort}
                      numeric
                    />
                    <SortableTh
                      column="source"
                      label={k('columns.source')}
                      sort={sort}
                      onSort={setSort}
                    />
                    <SortableTh
                      column="updated"
                      label={k('columns.updated')}
                      sort={sort}
                      onSort={setSort}
                      drop={1}
                    />
                    <th scope="col" data-numeric>
                      {k('columns.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.id}>
                      <td>{portLabel(row.portA)}</td>
                      <td>{portLabel(row.portB)}</td>
                      <td data-numeric>{n(row.distanceNm, 1)}</td>
                      <td>
                        <SourceBadge source={row.source} stored />
                      </td>
                      <td data-drop={1}>{formatStamp(row.updatedAt)}</td>
                      <td data-numeric>
                        {editor ? (
                          <Menu position="bottom-end" withinPortal>
                            <Menu.Target>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                aria-label={k('actionsOf', { pair: `${row.portA}–${row.portB}` })}
                              >
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={() => setDialog({ kind: 'history', row })}>
                                {k('history')}
                              </Menu.Item>
                              <Menu.Item onClick={() => setDialog({ kind: 'edit', row })}>
                                {k('edit')}
                              </Menu.Item>
                              {row.source === 'computed' ? (
                                <Menu.Item onClick={() => void recompute(row)}>
                                  {k('recompute')}
                                </Menu.Item>
                              ) : null}
                              <Menu.Item color="red" onClick={() => void remove(row)}>
                                {k('remove')}
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>

      {dialog?.kind === 'edit' && editor ? (
        <DistanceDialog
          editing={dialog.row}
          editor={editor}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            setNotice({ tone: 'success', text: k('saved') });
            void load();
          }}
        />
      ) : null}
      {dialog?.kind === 'history' && editor ? (
        <HistoryDialog
          title={`${dialog.row.portA}–${dialog.row.portB}`}
          load={() => editor.history(dialog.row.id)}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'import' && editor ? (
        <ImportDialog
          editor={editor}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            setNotice({ tone: 'success', text: t('ports.distances.importDialog.done') });
            void load();
          }}
        />
      ) : null}
    </div>
  );
}

function CompanyPortTable({ source }: { source: PortsSource }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`ports.distances.ports.${key}`, options);
  const errorText = (code: string) =>
    t(`ports.errors.${code}`, { defaultValue: t('ports.errors.GENERIC') });
  const countryName = useCountryName();
  const { refresh } = usePortCatalog();
  const editor = source.editor;
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; rows: CompanyPort[] }
  >({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<CompanyPort | 'new' | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const tableBox = useFitColumns<HTMLElement>();

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', rows: await source.companyPorts() });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);

  const rows = state.kind === 'ready' ? state.rows : [];
  const q = normalizeSearch(query);
  const items = rows.filter(
    (row) => !q || [row.code, row.name].some((value) => normalizeSearch(value).includes(q)),
  );
  const remove = async (row: CompanyPort) => {
    if (!editor) return;
    try {
      await editor.removePort(row.id, row.rowVersion);
      setNotice({ tone: 'success', text: k('removed') });
      refresh();
      await load();
    } catch (error) {
      setNotice({ tone: 'danger', text: errorText(errorCode(error)) });
    }
  };

  return (
    <div className={classes.tabs}>
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <Panel flush aria-label={k('table')}>
        <div className={purchasing.searchRow}>
          <TextInput
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
          {editor ? (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
              {k('add')}
            </Button>
          ) : null}
        </div>
        {state.kind === 'error' ? (
          <LoadError code={state.code} onRetry={() => void load()} />
        ) : state.kind === 'loading' ? (
          <TableSkeleton columns={6} label={k('table')} />
        ) : rows.length === 0 ? (
          <EmptyState title={k('emptyTitle')} description={k('emptyText')} />
        ) : (
          <section
            ref={tableBox}
            className={table.tableWrap}
            aria-label={k('table')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              <thead>
                <tr>
                  <th scope="col">{k('columns.code')}</th>
                  <th scope="col">{k('columns.name')}</th>
                  <th scope="col">{k('columns.country')}</th>
                  <th scope="col" data-drop={1}>
                    {k('columns.position')}
                  </th>
                  <th scope="col">{k('columns.euEea')}</th>
                  {editor ? (
                    <th scope="col" data-numeric>
                      {k('columns.actions')}
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className={classes.code}>{row.code}</span>
                    </td>
                    <td className={table.itemName}>{row.name}</td>
                    <td>{countryName(row.country)}</td>
                    <td data-drop={1}>
                      {formatPosition(row.lat, 'lat')} {formatPosition(row.lon, 'lon')}
                    </td>
                    <td>{row.euEea ? k('yes') : k('no')}</td>
                    {editor ? (
                      <td data-numeric>
                        <Menu position="bottom-end" withinPortal>
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              aria-label={k('actionsOf', { code: row.code })}
                            >
                              <IconDots size={16} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item onClick={() => setEditing(row)}>
                              {t('ports.distances.edit')}
                            </Menu.Item>
                            <Menu.Item color="red" onClick={() => void remove(row)}>
                              {t('ports.distances.remove')}
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </Panel>
      {editing && editor ? (
        <CompanyPortDialog
          editing={editing === 'new' ? null : editing}
          editor={editor}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setNotice({ tone: 'success', text: k('saved') });
            refresh();
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
